// Numbered-list editing for plain <textarea> fields.
//
// The Vision Board is a single textarea, so writing a list there meant typing the
// number, the dot and the newline by hand, and renumbering everything below by hand
// after inserting a line in the middle. These helpers give it the Enter behaviour
// people expect from a notes app, without pulling in a rich-text editor.
//
// Every function is pure: (text, selection) in, { value, selectionStart } out. The
// caller sets the textarea value and then restores the caret.

// "3. " / "3) " / "  10.  " — captures indent, number, delimiter and the content.
const NUMBERED_LINE = /^(\s*)(\d+)([.)])[ \t]+(.*)$/;

export const parseNumberedLine = (line) => {
  const m = NUMBERED_LINE.exec(line ?? '');
  if (!m) return null;
  return { indent: m[1], number: Number(m[2]), delimiter: m[3], content: m[4] };
};

export const isNumberedLine = (line) => parseNumberedLine(line) !== null;

const lineBoundsAt = (text, index) => {
  const start = text.lastIndexOf('\n', Math.max(0, index - 1)) + 1;
  const nlAt = text.indexOf('\n', index);
  return { start, end: nlAt === -1 ? text.length : nlAt };
};

// Rewrites the numbers of the contiguous numbered run containing `lineIndex` so they
// read 1, 2, 3… from wherever that run starts. A blank line or an unnumbered line
// ends the run, which is what keeps two separate lists on one page independent.
export const renumberRunAt = (lines, lineIndex) => {
  const out = [...lines];
  if (!isNumberedLine(out[lineIndex])) return out;

  let first = lineIndex;
  while (first > 0 && isNumberedLine(out[first - 1])) first -= 1;
  let last = lineIndex;
  while (last < out.length - 1 && isNumberedLine(out[last + 1])) last += 1;

  // The run keeps whatever number it already started on, so a list the user
  // deliberately began at 0 or 5 is not silently reset to 1.
  const startNum = parseNumberedLine(out[first]).number;
  for (let i = first; i <= last; i += 1) {
    const parsed = parseNumberedLine(out[i]);
    out[i] = `${parsed.indent}${startNum + (i - first)}${parsed.delimiter} ${parsed.content}`;
  }
  return out;
};

// What Enter should do inside a numbered list. Returns null when the caret is not on
// a numbered line, which means "let the browser insert a plain newline".
export const continueNumberedList = (text, selectionStart, selectionEnd = selectionStart) => {
  const value = typeof text === 'string' ? text : '';
  const caret = Math.max(0, Math.min(value.length, Number(selectionStart) || 0));
  const caretEnd = Math.max(caret, Math.min(value.length, Number(selectionEnd) || caret));

  const { start, end } = lineBoundsAt(value, caret);
  const parsed = parseNumberedLine(value.slice(start, end));
  if (!parsed) return null;

  // Enter on an empty "4. " ends the list rather than making an empty "5. ".
  if (!parsed.content.trim()) {
    const next = `${value.slice(0, start)}${parsed.indent}${value.slice(end)}`;
    return { value: next, selectionStart: start + parsed.indent.length };
  }

  const insertAt = Math.max(caret, start);
  const marker = `${parsed.indent}${parsed.number + 1}${parsed.delimiter} `;
  const withLine = `${value.slice(0, insertAt)}\n${marker}${value.slice(caretEnd)}`;

  // The new line pushes every following item down by one, so renumber the run.
  const lines = withLine.split('\n');
  const newLineIndex = withLine.slice(0, insertAt + 1).split('\n').length - 1;
  const renumbered = renumberRunAt(lines, newLineIndex).join('\n');

  return { value: renumbered, selectionStart: insertAt + 1 + marker.length };
};

// The "1." button: number every line of the selection (or of the paragraph the caret
// sits in), or strip the numbers if they are all numbered already.
export const toggleNumberedList = (text, selectionStart, selectionEnd = selectionStart) => {
  const value = typeof text === 'string' ? text : '';
  const from = Math.max(0, Math.min(value.length, Number(selectionStart) || 0));
  const to = Math.max(from, Math.min(value.length, Number(selectionEnd) || from));

  const blockStart = lineBoundsAt(value, from).start;
  const blockEnd = lineBoundsAt(value, to).end;
  const lines = value.slice(blockStart, blockEnd).split('\n');

  const meaningful = lines.filter((l) => l.trim());
  if (!meaningful.length) return null;

  const allNumbered = meaningful.every(isNumberedLine);
  let counter = 0;
  const rewritten = lines.map((line) => {
    if (!line.trim()) return line;
    const parsed = parseNumberedLine(line);
    if (allNumbered) return `${parsed.indent}${parsed.content}`;
    counter += 1;
    const indent = (line.match(/^\s*/) || [''])[0];
    return `${indent}${counter}. ${parsed ? parsed.content : line.trim()}`;
  });

  const block = rewritten.join('\n');
  return {
    value: `${value.slice(0, blockStart)}${block}${value.slice(blockEnd)}`,
    selectionStart: blockStart + block.length
  };
};
